import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, downloadPdfFile } from "../lib/api";
import { track } from "../lib/analytics";
import { Tip } from "../components/ui-bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Zap, CheckCircle2, XCircle, ArrowRight, PhoneCall, IndianRupee, ChevronDown, ChevronUp, Download } from "lucide-react";
import { toast } from "sonner";

const STATES = ["Andhra Pradesh", "Chhattisgarh", "Gujarat", "Haryana", "Karnataka", "Madhya Pradesh", "Maharashtra", "Punjab", "Rajasthan", "Tamil Nadu", "Uttar Pradesh"];

const fmt = (n) => {
  const abs = Math.abs(n);
  if (abs >= 100000) return `₹${(n / 100000).toFixed(1)} lakh`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
};

function Field({ label, tip, example, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-[#475569] flex items-center">
        {label}
        <Tip text={tip} example={example} />
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function OptionCard({ opt, best, onConsult, onRoadmap }) {
  const [open, setOpen] = useState(false);
  const positive = opt.monthly_savings_range[1] > 0;
  return (
    <div className={`bg-white rounded-xl border p-5 relative ${best ? "border-[#059669] border-2 shadow-lg" : "border-[#0B1F3A]/10"}`} data-testid={`analyzer-option-${opt.key}`}>
      {best && <span className="absolute -top-3 left-4 bg-[#059669] text-white text-[10px] font-bold uppercase tracking-wider rounded-full px-3 py-1">Deepest savings for you</span>}
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-serif font-semibold text-[#0B1F3A]">{opt.name}</h3>
        {opt.eligible ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#059669] bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 shrink-0" data-testid={`eligible-chip-${opt.key}`}><CheckCircle2 size={11} /> Eligible</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-500 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 shrink-0" data-testid={`ineligible-chip-${opt.key}`}><XCircle size={11} /> Not eligible</span>
        )}
      </div>
      <p className="text-xs text-[#475569] mt-1.5">{opt.plain}</p>
      <p className="text-[11px] text-[#0B1F3A] mt-2 bg-[#F5F8FF] border border-[#0B1F3A]/10 rounded-md px-3 py-2">{opt.why}</p>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">Indicative monthly {positive ? "savings" : "extra cost"}</p>
          <p className={`font-serif text-xl font-semibold ${positive ? "text-[#059669]" : "text-amber-600"}`} data-testid={`savings-${opt.key}`}>
            {opt.eligible ? (positive ? `${fmt(opt.monthly_savings_range[0])} – ${fmt(opt.monthly_savings_range[1])}` : `${fmt(Math.abs(opt.monthly_savings_range[0]))}`) : "—"}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">Landed cost</p>
          <p className="text-sm text-[#0B1F3A] mt-1">{opt.landed_cost}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-[#475569]">
        <span><b className="text-[#0B1F3A]">Effort:</b> {opt.effort}</span>
        <span><b className="text-[#0B1F3A]">Timeline:</b> {opt.timeline_note}</span>
        {opt.payback_years && <span><b className="text-[#0B1F3A]">Payback:</b> ~{opt.payback_years} yrs</span>}
        {opt.eligible && opt.replaces_pct > 0 && <span><b className="text-[#0B1F3A]">Replaces:</b> ~{opt.replaces_pct}% of your power</span>}
      </div>
      <p className="text-[11px] text-[#475569] mt-2">{opt.capex_note}</p>

      <button onClick={() => setOpen(!open)} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#1D4ED8]" data-testid={`toggle-details-${opt.key}`}>
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {open ? "Hide" : "Show"} approvals needed & state notes
      </button>
      {open && (
        <div className="mt-3 space-y-3 fade-up" data-testid={`details-${opt.key}`}>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">Regulatory steps for this route</p>
            <ol className="mt-1.5 space-y-1">
              {opt.steps.map((s, i) => <li key={s} className="text-xs text-[#0B1F3A] flex gap-2"><span className="font-bold text-[#B8860B]">{i + 1}.</span> {s}</li>)}
            </ol>
          </div>
          {opt.state_notes?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Your state's rules to know</p>
              {opt.state_notes.map((n) => <p key={n} className="text-[11px] text-amber-900 mt-1">{n}</p>)}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {opt.key !== "green_tariff" && (
              <button onClick={onRoadmap} className="inline-flex items-center gap-1.5 bg-[#1D4ED8] text-white rounded-md px-3 py-2 text-xs font-semibold" data-testid={`roadmap-cta-${opt.key}`}>
                Map the full approval roadmap <ArrowRight size={12} />
              </button>
            )}
            <button onClick={onConsult} className="inline-flex items-center gap-1.5 border border-[#059669] text-[#059669] rounded-md px-3 py-2 text-xs font-semibold" data-testid={`consult-cta-${opt.key}`}>
              <PhoneCall size={12} /> Validate with a Solarian expert
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Analyzer() {
  const [form, setForm] = useState({ state: "", monthly_kwh: "", contract_demand_kw: "", avg_tariff: "", roof_area_sqm: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", company: "" });
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/analyzer/compare", {
        state: form.state,
        monthly_kwh: parseFloat(form.monthly_kwh),
        contract_demand_kw: parseFloat(form.contract_demand_kw),
        avg_tariff: parseFloat(form.avg_tariff),
        roof_area_sqm: form.roof_area_sqm ? parseFloat(form.roof_area_sqm) : null,
      });
      setResult(data);
      track("analyzer_used", { state: form.state });
      setTimeout(() => document.getElementById("analyzer-results")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { type: "analyzer_interest", ...lead, note: `Green Power Switch Analyzer — ${form.state}, ${form.monthly_kwh} kWh/month @ ₹${form.avg_tariff}`, context: { answers: form } });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const downloadReport = async () => {
    if (!result?.id) return;
    try {
      await downloadPdfFile(`/analyzer/runs/${result.id}/pdf`, `RegNav Switch Analyzer - ${result.state}.pdf`);
      toast.success("Report downloaded");
      track("pdf_downloaded", { source: "analyzer" });
    } catch (err) {
      toast.error(apiError(err, "Couldn't download the report — try again"));
    }
  };

  const bestKey = result ? [...result.options].filter((o) => o.eligible).sort((a, b) => b.monthly_savings_range[1] - a.monthly_savings_range[1])[0]?.key : null;
  const inputCls = "w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#059669] focus:outline-none bg-white";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="analyzer-page">
      <div className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#059669] flex items-center gap-2"><Zap size={14} /> Green Power Switch Analyzer</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#0B1F3A] mt-2">Should your business switch to green power?</h1>
        <p className="text-sm text-[#475569] mt-3">Enter four numbers from your electricity bill and compare the four ways to go green in your state — savings, eligibility, effort and the exact approvals each route needs. No signup required.</p>
      </div>

      <form onSubmit={run} className="mt-8 bg-white border border-[#0B1F3A]/10 rounded-2xl p-5 sm:p-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="analyzer-form">
        <Field label="State" tip="Open access, banking and net-metering rules differ sharply by state — we apply your state's current regulations." example="Maharashtra">
          <select required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls} data-testid="analyzer-state-select">
            <option value="">Select your state…</option>
            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Monthly consumption (kWh)" tip="The 'units consumed' figure on your monthly electricity bill. One unit = 1 kWh." example="A mid-size factory uses 50,000–500,000 kWh/month">
          <input required type="number" min="1" placeholder="e.g., 100000" value={form.monthly_kwh} onChange={(e) => setForm({ ...form, monthly_kwh: e.target.value })} className={inputCls} data-testid="analyzer-kwh-input" />
        </Field>
        <Field label="Contract demand (kW)" tip="Your sanctioned/contracted demand — printed on the bill header. It decides open-access eligibility (100 kW threshold)." example="A 1 MVA connection ≈ 1000 kW">
          <input required type="number" min="1" placeholder="e.g., 500" value={form.contract_demand_kw} onChange={(e) => setForm({ ...form, contract_demand_kw: e.target.value })} className={inputCls} data-testid="analyzer-demand-input" />
        </Field>
        <Field label="Average tariff (₹/kWh)" tip="Total monthly bill ÷ units consumed. Include demand charges for a truer picture." example="HT industrial tariffs run ₹7–10/kWh in most states">
          <input required type="number" step="0.01" min="0.5" placeholder="e.g., 8.50" value={form.avg_tariff} onChange={(e) => setForm({ ...form, avg_tariff: e.target.value })} className={inputCls} data-testid="analyzer-tariff-input" />
        </Field>
        <Field label="Roof / open area (sq m) — optional" tip="If you know your usable roof or ground area, we size the on-site option to it (~10 sq m per kW). Leave blank to size by contract demand." example="A 5,000 sq m shed roof fits ~500 kW">
          <input type="number" min="0" placeholder="Leave blank if unsure" value={form.roof_area_sqm} onChange={(e) => setForm({ ...form, roof_area_sqm: e.target.value })} className={inputCls} data-testid="analyzer-roof-input" />
        </Field>
        <div className="flex items-end">
          <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#059669] text-white rounded-md px-4 py-2.5 text-sm font-bold hover:bg-[#047857] disabled:opacity-60 transition-colors" data-testid="analyzer-submit-btn">
            <IndianRupee size={15} /> {busy ? "Comparing…" : "Compare my 4 green-power routes"}
          </button>
        </div>
      </form>

      {result && (
        <div id="analyzer-results" className="mt-10 space-y-5 fade-up" data-testid="analyzer-results">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="font-serif text-2xl text-[#0B1F3A]">Your 4 routes in {result.state}</h2>
            <div className="flex items-center gap-3">
              <p className="text-xs text-[#475569]">Indicative planning numbers — hover the ⓘ icons anywhere for help</p>
              <button onClick={downloadReport} className="inline-flex items-center gap-1.5 border-2 border-[#059669] text-[#059669] rounded-md px-3.5 py-2 text-xs font-bold hover:bg-emerald-50 transition-colors" data-testid="analyzer-download-report-btn">
                <Download size={13} /> Download report (PDF)
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {result.options.map((o) => (
              <OptionCard key={o.key} opt={o} best={o.key === bestKey}
                onConsult={() => setConsultOpen(true)}
                onRoadmap={() => navigate("/wizard")} />
            ))}
          </div>
          <div className="bg-[#F5F8FF] border border-[#0B1F3A]/10 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-[#475569] font-semibold">How these numbers are built (assumptions)</p>
            {result.assumptions.map((a) => <p key={a} className="text-[11px] text-[#475569] mt-1">· {a}</p>)}
          </div>
          <div className="bg-[#0B1F3A] text-white rounded-xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-serif text-lg">Want these numbers validated against your actual bill & state charges?</p>
              <p className="text-xs text-white/60 mt-1">A Solarian expert runs the real open-access charge sheet for your discom — free first consult.</p>
            </div>
            <button onClick={() => setConsultOpen(true)} className="shrink-0 bg-[#059669] text-white rounded-md px-5 py-2.5 text-sm font-bold" data-testid="analyzer-expert-cta">Talk to an expert</button>
          </div>
        </div>
      )}

      <Dialog open={consultOpen} onOpenChange={setConsultOpen}>
        <DialogContent aria-describedby={undefined} data-testid="analyzer-consult-dialog">
          <DialogHeader><DialogTitle className="font-serif">Validate your switch with a Solarian expert</DialogTitle></DialogHeader>
          {sent ? (
            <p className="text-sm text-[#0B1F3A]" data-testid="analyzer-lead-confirmation">Thanks — a Solarian expert will reach out within 1 business day with a validated comparison for your state and discom.</p>
          ) : (
            <form onSubmit={submitLead} className="space-y-3">
              <input required placeholder="Your name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="analyzer-lead-name" />
              <input required type="email" placeholder="Work email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="analyzer-lead-email" />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="analyzer-lead-phone" />
                <input placeholder="Company" value={lead.company} onChange={(e) => setLead({ ...lead, company: e.target.value })} className="w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="analyzer-lead-company" />
              </div>
              <button type="submit" className="w-full bg-[#059669] text-white rounded-md py-2.5 text-sm font-bold" data-testid="analyzer-lead-submit">Request validation</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
